import { observer } from 'mobx-react-lite';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '../../global-state/themeContext';
import { useUser } from '../../hooks';
import { Icon, Icons, Tooltip, Typography } from '../../stories/atoms';
import { ColumnFlexContainer, ContainerV2 } from '../../stories/atoms/containerV2/ContainerV2';
import { Button, IconWithText, Modal } from '../../stories/molecules';
import { employeesTranslations } from '../../translations';
import { requestAccessTranslation } from '../../translations/selectRole';
import { convertCamelCaseToTitleCase } from '../../utils';
import { AvatarSettings } from '../safe-access-management/avatar-settings/AvatarSettings';
import { StyledPlusIcon } from './Profile.styles';
import { getCurrentJobTitles, getHasOrganizationIdInAssignments } from './UserInfoSection.util';
import { ProfileAvatar } from './ProfileAvatar';

type AddAccessActionProps = {
  isMobile?: boolean;
  hasOrganizationIdInAssignments: boolean;
  onAddAccess: () => void;
};

const AddAccessMobileTrigger: FC<AddAccessActionProps> = ({
  isMobile,
  hasOrganizationIdInAssignments,
  onAddAccess,
}) => {
  const { t } = useTranslation();
  const { addAccess } = requestAccessTranslation;

  if (!isMobile || !hasOrganizationIdInAssignments) {
    return null;
  }

  return (
    <Tooltip
      position="bottom"
      arrowedToolTip
      text={
        <ContainerV2 padding={[2]}>
          <Typography color="white">{t(addAccess.key, addAccess.defaultValue)}</Typography>
        </ContainerV2>
      }
    >
      <StyledPlusIcon icon={Icons.PLUS} onClick={onAddAccess} />
    </Tooltip>
  );
};

const AddAccessDesktopButton: FC<AddAccessActionProps> = ({
  isMobile,
  hasOrganizationIdInAssignments,
  onAddAccess,
}) => {
  const { t } = useTranslation();
  const { addAccess } = requestAccessTranslation;

  if (isMobile || !hasOrganizationIdInAssignments) {
    return null;
  }

  return (
    <ContainerV2 display="flex" justifyContent="end" alignItems="center">
      <Button
        label={t(addAccess.key, addAccess.defaultValue)}
        onClick={onAddAccess}
        buttonContentProps={{ iconOptions: { customIcon: <Icon icon={Icons.PLUS} /> } }}
      />
    </ContainerV2>
  );
};

type Props = {
  onAddAccess: () => void;
  isMobile?: boolean;
};

export const UserInfoSection: FC<Props> = observer(({ onAddAccess, isMobile }) => {
  const { mode } = useThemeContext();
  const { t } = useTranslation();

  const [isEditingAvatar, setIsEditingAvatar] = useState(false);

  const {
    get: { currentStoreId, profile },
  } = useUser();

  const { other } = employeesTranslations.employees;

  const currentJobTitles = getCurrentJobTitles(profile, currentStoreId, t, other);

  // Enable add access actions only if at least one assignment has an organizationId
  const hasOrganizationIdInAssignments = getHasOrganizationIdInAssignments(profile);

  return (
    <>
      {isEditingAvatar && (
        <Modal
          isOpen={isEditingAvatar}
          handleClose={() => setIsEditingAvatar(false)}
          isFullScreen
          transitionType="fade"
          hasCloseIcon
        >
          <ContainerV2 padding={isMobile ? [0] : [12]}>
            <AvatarSettings />
          </ContainerV2>
        </Modal>
      )}
      <ContainerV2
        data-testid="user-info-section"
        display="flex"
        flexDirection={isMobile ? 'column' : 'row'}
        backgroundColor={mode === 'dark' ? 'backgroundPaperDark' : 'white'}
        hasBorder
        borderRadius={[2]}
        padding={isMobile ? [4] : [8]}
        alignItems={isMobile ? 'start' : 'center'}
        width="100%"
      >
        {/* Avatar */}
        <ContainerV2
          data-testid="user-avatar-container"
          display="flex"
          flexDirection={isMobile ? 'row' : 'column'}
          alignItems="center"
          justifyContent="between"
          width={isMobile ? '100%' : 'auto'}
        >
          <ProfileAvatar
            avatarUrl={profile.avatar?.url}
            isMobile={isMobile}
            mode={mode}
            onEditAvatar={() => setIsEditingAvatar(true)}
          />

          <AddAccessMobileTrigger
            isMobile={isMobile}
            hasOrganizationIdInAssignments={hasOrganizationIdInAssignments}
            onAddAccess={onAddAccess}
          />
        </ContainerV2>

        {/* Info */}
        <ColumnFlexContainer margin={isMobile ? [1, 0, 0] : [0, 0, 0, 5]} width="100%">
          <ContainerV2 display="flex" alignItems="center">
            <Typography type={isMobile ? 'body4' : 'h3'} data-testid="user-full-name">
              {convertCamelCaseToTitleCase(`${profile.firstName} ${profile.lastName}`)}
            </Typography>

            {profile.isDeveloper && (
              <ContainerV2
                display="flex"
                backgroundColor="infoTransparent"
                padding={[1, 2]}
                borderRadius={[2]}
                margin={[0, 0, 0, 2]}
              >
                <Typography type={isMobile ? 'legal' : 'caption'} color="infoDark">
                  {'</> Developer'}
                </Typography>
              </ContainerV2>
            )}
          </ContainerV2>

          <Typography type={isMobile ? 'caption' : 'body3'} color="textGray" data-testid="current-store-title">
            {`${currentJobTitles}`}
          </Typography>

          <ContainerV2
            display="flex"
            flexDirection={isMobile ? 'column' : 'row'}
            margin={[2, 0, 0]}
            gap={[3]}
            justifyContent="between"
            alignItems="start"
            width="100%"
          >
            {/* Contact Info */}
            <ContainerV2 display="flex" flexDirection="row" flexWrap="wrap" gap={[3]}>
              <IconWithText
                label={profile.upn}
                iconOptions={{ icon: Icons.EMAIL, size: 'small' }}
                textOptions={{ type: 'body3', copyable: true, copyText: profile.upn }}
                backgroundColor={mode === 'dark' ? 'backgroundSelectedHover' : 'backgroundTableHeader'}
                padding={[2, 3]}
                containerStyles={{ borderRadius: '4px' }}
              />
            </ContainerV2>

            <AddAccessDesktopButton
              isMobile={isMobile}
              hasOrganizationIdInAssignments={hasOrganizationIdInAssignments}
              onAddAccess={onAddAccess}
            />
          </ContainerV2>
        </ColumnFlexContainer>
      </ContainerV2>
    </>
  );
});
